import React from 'react'

/**
 * Custom React components for rendering markdown elements.
 * These components provide consistent styling across the AI chat interface.
 */

// Heading components
export const H1 = ({ children, ...props }: any) => (
  <h1 className='mb-4 mt-6 text-xl font-bold text-gray-900 dark:text-gray-100' {...props}>
    {children}
  </h1>
)

export const H2 = ({ children, ...props }: any) => (
  <h2 className='mb-3 mt-5 text-lg font-semibold text-gray-900 dark:text-gray-100' {...props}>
    {children}
  </h2>
)

export const H3 = ({ children, ...props }: any) => (
  <h3 className='mb-2 mt-4 text-base font-medium text-gray-900 dark:text-gray-100' {...props}>
    {children}
  </h3>
)

export const H4 = ({ children, ...props }: any) => (
  <h4 className='mb-2 mt-3 text-sm font-medium text-gray-900 dark:text-gray-100' {...props}>
    {children}
  </h4>
)

export const H5 = ({ children, ...props }: any) => (
  <h5 className='mb-2 mt-3 text-sm font-medium text-gray-900 dark:text-gray-100' {...props}>
    {children}
  </h5>
)

export const H6 = ({ children, ...props }: any) => (
  <h6 className='mb-2 mt-3 text-sm font-medium text-gray-900 dark:text-gray-100' {...props}>
    {children}
  </h6>
)

// Code components
export const Code = ({ className, children, ...props }: any) => {
  const match = /language-(\w+)/.exec(className ?? '')
  const isInline = !match
  
  return isInline ? (
    <code className='rounded bg-amber-50 px-1 py-0.5 text-xs dark:bg-amber-950/30' {...props}>
      {children}
    </code>
  ) : (
    <pre className='rounded-md bg-amber-50 p-3 dark:bg-amber-950/30'>
      <code className={className} {...props}>
        {children}
      </code>
    </pre>
  )
}

// Link component
export const Link = ({ children, href, ...props }: any) => (
  <a
    href={href}
    target='_blank'
    rel='noopener noreferrer'
    className='text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300'
    {...props}
  >
    {children}
  </a>
)

// List components
export const UnorderedList = ({ children, className, ...props }: any) => {
  // Check if this is a task list
  const isTaskList = className?.includes('contains-task-list')

  if (isTaskList) {
    return (
      <ul className='mb-4 space-y-2 pl-5' {...props}>
        {children}
      </ul>
    )
  }

  return (
    <ul className='mb-4 list-disc space-y-2 pl-5' {...props}>
      {children}
    </ul>
  )
}

export const OrderedList = ({ children, ...props }: any) => (
  <ol className='mb-4 list-decimal space-y-2 pl-5' {...props}>
    {children}
  </ol>
)

export const ListItem = ({ children, className, ...props }: any) => {
  // Check if this is a task list item
  const isTaskItem = className?.includes('task-list-item')

  if (isTaskItem) {
    return (
      <li className='flex items-start gap-3 text-base leading-relaxed' {...props}>
        {/* Bigger empty circle for tasks */}
        <div className='mt-1.5 h-4 w-4 flex-shrink-0 rounded-full border-[1.5px] border-amber-600 bg-transparent'></div>
        <span className='flex-1'>
          {React.Children.toArray(children).filter(
            (child) => !(React.isValidElement(child) && child.type === 'input')
          )}
        </span>
      </li>
    )
  }

  return (
    <li className='flex items-start gap-3 text-base leading-relaxed' {...props}>
      {/* Small orange circle for regular bullets */}
      <div className='mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-orange-500'></div>
      <span className='flex-1'>{children}</span>
    </li>
  )
}

// Text components
export const Paragraph = ({ children, ...props }: any) => (
  <p className='mb-3 text-[15px] leading-relaxed' {...props}>
    {children}
  </p>
)

export const Blockquote = ({ children, ...props }: any) => (
  <blockquote
    className='border-l-4 border-amber-500 pl-4 italic text-gray-600 dark:text-gray-400'
    {...props}
  >
    {children}
  </blockquote>
)

// Table components with error handling
export const Table = ({ children, ...props }: any) => {
  try {
    return (
      <div className='overflow-x-auto'>
        <table
          className='min-w-full border-collapse border border-amber-200 dark:border-amber-700'
          {...props}
        >
          {children}
        </table>
      </div>
    )
  } catch (error) {
    console.warn('Table rendering failed, using fallback:', error)
    return (
      <div className='overflow-x-auto rounded border border-amber-200 p-2 dark:border-amber-700'>
        <div className='text-sm text-gray-600 dark:text-gray-400'>
          [Table content - rendering failed]
        </div>
      </div>
    )
  }
}

export const TableHeader = ({ children, ...props }: any) => {
  try {
    return (
      <th
        className='border border-amber-200 bg-amber-50 px-3 py-2 text-left font-medium dark:border-amber-700 dark:bg-amber-950/30'
        {...props}
      >
        {children}
      </th>
    )
  } catch {
    return (
      <th className='border border-amber-200 bg-amber-50 px-3 py-2 text-left font-medium dark:border-amber-700 dark:bg-amber-950/30'>
        {children}
      </th>
    )
  }
}

export const TableCell = ({ children, ...props }: any) => {
  try {
    return (
      <td className='border border-amber-200 px-3 py-2 dark:border-amber-700' {...props}>
        {children}
      </td>
    )
  } catch {
    return (
      <td className='border border-amber-200 px-3 py-2 dark:border-amber-700'>
        {children}
      </td>
    )
  }
}

/**
 * Map of markdown element names to their custom components.
 * Used with ReactMarkdown's components prop.
 */
export const markdownComponents = {
  h1: H1,
  h2: H2,
  h3: H3,
  h4: H4,
  h5: H5,
  h6: H6,
  code: Code,
  a: Link,
  ul: UnorderedList,
  ol: OrderedList,
  li: ListItem,
  p: Paragraph,
  blockquote: Blockquote,
  table: Table,
  th: TableHeader,
  td: TableCell,
}